import {
  AnnouncementEmojiFragment,
  AnnouncementFragment,
  HubAnnouncementFieldsFragment,
  useCreateAnnouncementEmojiMutation,
  useDeleteAnnouncementEmojiMutation,
  useMarkHubAnnouncementAsReadMutation,
} from '../../../__generated__/graphql';
import { AnnouncementType } from './types';
import { groupBy, orderBy } from 'lodash';
import { Emoji, EMOJI_OPERATIONS } from '../../../components/Announcement';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutationErrorHandler } from '../../../../../hooks/useMutationErrorHandler';
import { useUserInfo } from '../../../context/UserInfo';

export const searchItems = (
  announcements: AnnouncementType[],
  searchText: string,
): AnnouncementType[] => {
  if (searchText === '') {
    return announcements;
  }
  return announcements.filter((a) =>
    a.title.toLowerCase().includes(searchText.toLowerCase()),
  );
};

export const mapAnnouncement = (a: AnnouncementFragment): AnnouncementType => {
  const emojis = groupBy(a.emojis.map(mapEmoji), 'name');
  return {
    id: a.Id,
    title: a.Name,
    createdDate: new Date(a.CreatedDate),
    publishedDate: a.flair__Published_Date__c
      ? new Date(a.flair__Published_Date__c)
      : null,
    isPinned: Boolean(a.flair__Pinned__c),
    body: a.flair__Announcement_Text__c ?? '',
    emojis,
    employee: a.employee
      ? {
          name: a.employee.Name,
          avatarUrl: a.employee.avatar?.url ?? '',
        }
      : null,
    commentsCount: a.commentsCount,
    isTracked: false,
    isRead: false,
    notificationId: null,
    requireAcknowledgment: false,
    attachments: a.ContentDocumentLinks,
  };
};

export const mapHubAnnouncements = (
  announcements?: ReadonlyArray<HubAnnouncementFieldsFragment>,
): AnnouncementType[] => {
  if (!announcements) {
    return [];
  }
  return orderBy(
    announcements.map(mapHubAnnouncement),
    [(item) => (item.isPinned ? 1 : 0), (item) => item.createdDate],
    ['desc', 'desc'],
  );
};

const mapHubAnnouncement = (
  a: HubAnnouncementFieldsFragment,
): AnnouncementType => {
  const emojis = groupBy(a.emojis.map(mapEmoji), 'name');
  return {
    id: a.id,
    title: a.title,
    createdDate: a.publishedDate ? new Date(a.publishedDate) : new Date(),
    publishedDate: a.publishedDate ? new Date(a.publishedDate) : null,
    isPinned: a.isPinned,
    body: a.text ?? '',
    emojis,
    employee: a.employee
      ? {
          name: a.employee.Name,
          avatarUrl: a.employee.avatar?.url ?? '',
        }
      : null,
    commentsCount: a.commentsCount,
    isTracked: a.isTracked,
    isRead: a.isRead,
    notificationId: a.notificationId ?? null,
    requireAcknowledgment: a.requireAcknowledgment,
    attachments: a.attachments,
  };
};

const mapEmoji = (src: AnnouncementEmojiFragment): Emoji => ({
  id: src.Id,
  name: src.Name,
  recordId: src.flair__Company_Announcement__c,
  employee: {
    id: src.employee.Id,
    name: src.employee.Name,
  },
});

export const useMarkAsRead = (announcements: AnnouncementType[]) => {
  const [markAsRead] = useMarkHubAnnouncementAsReadMutation();
  const markedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const toMark = announcements.filter(
      (a): a is AnnouncementType & { notificationId: string } =>
        a.isTracked &&
        !a.isRead &&
        !!a.notificationId &&
        !markedIdsRef.current.has(a.notificationId),
    );
    if (toMark.length === 0) {
      return;
    }
    toMark.forEach((a) => markedIdsRef.current.add(a.notificationId));

    toMark.forEach((a) => {
      markAsRead({ variables: { notificationId: a.notificationId } }).catch(
        () => {
          markedIdsRef.current.delete(a.notificationId);
        },
      );
    });
  }, [announcements, markAsRead]);
};

export const useEmojiChange = (announcements: AnnouncementType[]) => {
  const { id: employeeId } = useUserInfo();

  const [createEmoji] = useCreateAnnouncementEmojiMutation({
    update: (cache) => {
      cache.evict({ fieldName: 'hubAnnouncements' });
      cache.evict({ fieldName: 'lastUpdates' });
    },
  });
  const [deleteEmoji] = useDeleteAnnouncementEmojiMutation({
    update: (cache) => {
      cache.evict({ fieldName: 'hubAnnouncements' });
      cache.evict({ fieldName: 'lastUpdates' });
    },
  });
  const errorHandler = useMutationErrorHandler();
  const [updatingItems, setUpdatingItems] = useState<string[]>([]);

  const onEmojiChange = useCallback(
    (data: Pick<any, 'name' | 'recordId'>, operation: string) => {
      const announcementId = data.recordId;
      const emojis = announcements.find((a) => a.id === announcementId)?.emojis;

      const emoji = emojis?.[data.name]?.find(
        (e) => e.name === data.name && e.employee.id === employeeId,
      );

      if (emojis) {
        switch (operation) {
          case EMOJI_OPERATIONS.CREATE:
            // If emoji is not selected before
            if (!emoji) {
              setUpdatingItems((prev) => [...prev, announcementId]);
              createEmoji({
                variables: {
                  input: {
                    name: data.name,
                    announcementId,
                    employeeId: employeeId,
                  },
                },
              })
                .catch(errorHandler)
                .finally(() => {
                  setUpdatingItems((prev) =>
                    prev.filter((x) => x !== announcementId),
                  );
                });
            }
            break;
          case EMOJI_OPERATIONS.DELETE:
            if (emoji) {
              setUpdatingItems((prev) => [...prev, announcementId]);
              deleteEmoji({
                variables: {
                  input: { id: emoji.id },
                },
              })
                .catch(errorHandler)
                .finally(() => {
                  setUpdatingItems((prev) =>
                    prev.filter((x) => x !== announcementId),
                  );
                });
            }
            break;
        }
      }
    },
    [announcements, createEmoji, deleteEmoji, employeeId, errorHandler],
  );

  return { onEmojiChange, updatingItems };
};
